import { useTranslation } from 'react-i18next';
import { useDocumentParams } from 'app/router/params';
import { useState } from 'react';
import { useUser } from 'app/state/contexts';
import { useGetDocument } from 'entities/document';
import { useImagesInDocument } from 'entities/document';
import { DataImage } from 'app/components';
import Modal from 'react-bootstrap/Modal';

interface BackgroundImagePickerProps {
  value: string;
  onChange: (key: string, value: string) => void;
  variableKey: string;
}

interface PickerImageFile {
  superId: string;
  fileType: string;
  fileContent: string;
  filename?: string;
}

function extractDataUri(value: string): string | null {
  const match = value.match(/url\(['"]?(data:[^'"]+)['"]?\)/);
  return match ? match[1] : null;
}

export function BackgroundImagePicker({ value, onChange, variableKey }: BackgroundImagePickerProps) {
  const { t } = useTranslation('dc');

  const [showModal, setShowModal] = useState(false);
  const { documentId } = useDocumentParams();
  const { selectedOrganization } = useUser();
  const pDocument = useGetDocument(documentId, selectedOrganization);
  const { data: images, isLoading } = useImagesInDocument(pDocument.data?.superId);

  const hasImage = value && value !== 'none';
  const previewSrc = hasImage ? extractDataUri(value) : null;

  const handleSelect = (file: PickerImageFile) => {
    const dataUri = `data:${file.fileType};base64,${file.fileContent}`;
    onChange(variableKey, `url('${dataUri}')`);
    setShowModal(false);
  };

  const handleClear = () => {
    onChange(variableKey, 'none');
  };

  return (
    <div className="d-flex align-items-center gap-2">
      {previewSrc && <img src={previewSrc} alt="" className="border rounded" style={{ width: 40, height: 40, objectFit: 'contain' }} />}
      <button className="btn btn-sm btn-outline-primary" onClick={() => setShowModal(true)}>
        <i className="fa-regular fa-image me-1" />
        {hasImage ? t('bg.change') : t('bg.select')}
      </button>
      {hasImage && (
        <button className="btn btn-sm btn-outline-danger" onClick={handleClear}>
          <i className="fa-regular fa-xmark" />
        </button>
      )}

      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>{t('bg.modal-title')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {isLoading ? (
            <p className="text-muted">{t('bg.loading')}</p>
          ) : !images?.length ? (
            <p className="text-muted">{t('bg.no-images')}</p>
          ) : (
            <div className="d-flex flex-wrap gap-2">
              {images.map((image) => {
                const file = image.file as PickerImageFile;
                return (
                  <div key={file.superId} className="border rounded p-1" style={{ width: 120, cursor: 'pointer' }} onClick={() => handleSelect(file)}>
                    <DataImage {...file} style={{ width: '100%', height: 80, objectFit: 'contain', display: 'block' }} />
                    <div className="text-truncate fs-7 text-muted mt-1">{file?.filename}</div>
                  </div>
                );
              })}
            </div>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
}
